import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);

/**
 * Reusable KPI Card Component
 * @param {string} title - Card title
 * @param {number|string} value - Card value
 * @param {ReactNode} icon - Icon component
 * @param {string} color - Background color
 * @param {number} delay - Animation delay
 * @param {Function} onClick - Click handler
 */

interface KPICardProps {
  title: any;
  value: any;
  icon: any;
  color: any;
  delay?: number;
  onClick?: any;
}

function KPICard({ title, value, icon, color, delay = 0, onClick }: KPICardProps) {
  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      onClick={onClick}
      sx={{ 
        height: '100%', 
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`, 
        color: 'white',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
          transition: 'all 0.3s ease',
        },
      }}
    >
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 120,
          height: 120,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        }}
      />

      <motion.div
        animate={{
          x: ['-100%', '200%'],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          repeatDelay: 2,
          ease: 'easeInOut',
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '50%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
          transform: 'skewX(-20deg)',
        }}
      />

      <CardContent sx={{ position: 'relative', zIndex: 1, py: 2.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 500, fontSize: '0.813rem' }}>
              {title}
            </Typography>
            <motion.div
              animate={{
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <Typography variant="h4" component="div" sx={{ fontWeight: 700, fontSize: '2rem' }}>
                {value}
              </Typography>
            </motion.div>
          </Box>
          <motion.div
            animate={{
              rotate: [0, 5, 0, -5, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <Box 
              sx={{ 
                fontSize: 48, 
                opacity: 0.9,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 2.5,
                p: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          </motion.div>
        </Box>
      </CardContent>
    </MotionCard>
  );
}

export default KPICard;
